/**
 * The HUD shell (spec §6.2).
 *
 * React draws the HUD only; nothing here runs per frame and nothing here computes
 * physics (spec §9.1, CLAUDE.md style rules). Numbers the simulation has not
 * produced yet render as a dash with their unit still shown, so the layout is
 * honest about what M0 does and does not simulate.
 */

import { SPEEDS, useGameStore } from '../state/store.js';
import styles from './hud.module.css';

/** Tool categories from spec §6.2, with the milestone that unlocks each. */
const TOOL_CATEGORIES: readonly { readonly label: string; readonly milestone: string }[] = [
  { label: 'Terrain', milestone: 'M1' },
  { label: 'Waterways', milestone: 'M3' },
  { label: 'Structures', milestone: 'M3' },
  { label: 'Pipes', milestone: 'M5' },
  { label: 'Machines', milestone: 'M4' },
  { label: 'Power', milestone: 'M4' },
  { label: 'Farms', milestone: 'M2' },
  { label: 'Info', milestone: 'M1' },
  { label: 'Bulldoze', milestone: 'M1' },
];

function Value({
  value,
  unit,
  digits = 0,
}: {
  readonly value: number | undefined;
  readonly unit?: string | undefined;
  readonly digits?: number | undefined;
}): React.JSX.Element {
  return (
    <span className={styles.statValue}>
      {value === undefined
        ? '—'
        : value.toLocaleString('en', {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          })}
      {unit !== undefined && <span className={styles.unit}> {unit}</span>}
    </span>
  );
}

function Row({
  label,
  value,
  unit,
  digits = 0,
  volatile: isVolatile = false,
}: {
  readonly label: string;
  readonly value: number | undefined;
  readonly unit?: string | undefined;
  readonly digits?: number | undefined;
  /** Changes every frame; golden screenshots mask these (spec §10.4). */
  readonly volatile?: boolean | undefined;
}): React.JSX.Element {
  return (
    <>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue} {...(isVolatile ? { 'data-volatile': 'true' } : {})}>
        <Value value={value} unit={unit} digits={digits} />
      </span>
    </>
  );
}

export function Hud(): React.JSX.Element {
  const speed = useGameStore((s) => s.speed);
  const setSpeed = useGameStore((s) => s.setSpeed);
  const render = useGameStore((s) => s.render);
  const sim = useGameStore((s) => s.sim);

  return (
    <div className={styles.root}>
      <header className={`${styles.panel} ${styles.top}`}>
        <span className={styles.brand}>Hydro-Terra</span>

        <span className={styles.stat}>
          <span className={styles.statLabel}>Funds</span>
          <Value value={sim.fundsUsd} unit="USD" />
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Day</span>
          <Value value={sim.day} />
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Air</span>
          <Value value={sim.airTempC} unit="°C" digits={1} />
        </span>

        <span className={styles.speeds}>
          {SPEEDS.map((s) => (
            <button
              key={s.id}
              type="button"
              title={s.title}
              aria-pressed={speed === s.id}
              className={`${styles.speedButton} ${speed === s.id ? styles.speedButtonActive : ''}`}
              onClick={() => setSpeed(s.id)}
            >
              {s.label}
            </button>
          ))}
        </span>

        <span className={styles.stat}>
          <span className={styles.statLabel}>WRSI</span>
          <Value value={sim.wrsi} unit="%" />
          <span
            className={styles.meter}
            role="meter"
            aria-label="Aggregate water requirement satisfaction"
            aria-valuenow={sim.wrsi ?? 0}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className={styles.meterFill} style={{ width: `${sim.wrsi ?? 0}%` }} />
          </span>
        </span>
      </header>

      <aside className={`${styles.panel} ${styles.advisors}`}>
        <h2 className={styles.panelTitle}>Advisors</h2>
        <p className={styles.note}>
          The Hydrologist, Structural Engineer, Agronomist and Treasurer start reporting once there
          is water to report on. The solver lands in M1.
        </p>
      </aside>

      <aside className={`${styles.panel} ${styles.inspector}`}>
        <h2 className={styles.panelTitle}>Renderer</h2>
        <div className={styles.rows}>
          <Row label="Frame rate" value={render.fps} unit="fps" volatile />
          <Row label="Frame time" value={render.frameMs} unit="ms" digits={1} volatile />
          <Row label="Altitude" value={render.altitudeM} unit="m" volatile />
          <Row label="Tilt" value={render.tiltDeg} unit="°" />
          <Row label="Vertices" value={render.vertexCount} />
          <span className={styles.rowLabel}>Backend</span>
          <span className={styles.rowValue}>{render.backend}</span>
        </div>

        <h2 className={styles.panelTitle} style={{ marginTop: '0.875rem' }}>
          Simulation
        </h2>
        <div className={styles.rows}>
          <Row label="Surface storage" value={sim.storageM3} unit="m³" />
        </div>
        <p className={styles.note}>
          Cell depth, velocity, Froude number and soil moisture appear here, each with an Explain
          panel, once the shallow-water solver is wired to the worker in M1.
        </p>
      </aside>

      <nav className={`${styles.panel} ${styles.tools}`} aria-label="Tools">
        {TOOL_CATEGORIES.map((t) => (
          <button key={t.label} type="button" className={styles.tool} disabled>
            {t.label}
            <span className={styles.toolLock}>{t.milestone}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
